import { v4 as uuidv4 } from 'uuid';
import qs from 'qs';
import { history } from 'store';

import { isTrackedUser } from './context';

// One "search journey" within a session: a search and everything that flows
// from it, until a new search starts a new journey.
//
// Storage model:
//  - sessionStorage + in-memory are the source of truth for same-tab continuity.
//  - The URL query param `ssid` is a HANDOFF channel only: result links are
//    decorated with it (see withSsid) so a manually-opened new tab can adopt
//    the journey, after which it is immediately stripped from the address bar.
//    It never persists in the visible URL.
//
// `ssid` is a FE-only marker: it must never be forwarded to the backend as a
// query param (see getOffersSearch, which strips it) — the API rejects it.
//
// All minting / URL decoration is gated on isTrackedUser() (TA / SR only).
export const SUBSESSION_KEY = 'pe_tracking_sub_session_id';
export const SSID_PARAM = 'ssid';

// JOURNEY_PATHS are only pages that take part in a tracked "events", and therefore the only
// pages where the ssid is carried in/out of the URL (boot capture + link
// decorator). Everywhere else the ssid simply isn't relevant, so we don't touch
// the URL or decorate links there.
const JOURNEY_PATHS = ['/search', '/booking-builder', '/basket'];

const currentPathname = (): string => {
  try {
    return window.location.pathname;
  } catch {
    return '';
  }
};

export const isJourneyPath = (pathname: string): boolean =>
  JOURNEY_PATHS.some(p => pathname.startsWith(p));

let current: string | null = null;

const readSs = (key: string): string | null => {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeSs = (key: string, value: string): void => {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    /* tracking must never throw */
  }
};

export const readSsidFromUrl = (): string | null => {
  try {
    const parsed = qs.parse(window.location.search, { ignoreQueryPrefix: true });
    const value = parsed[SSID_PARAM];
    return typeof value === 'string' && value.length > 0 ? value : null;
  } catch {
    return null;
  }
};

// ssid handed over from the opener tab, captured (and stripped from the
// address bar) at module evaluation i.e. before React mounts, before router
// redirects, and before query-string sagas (e.g. updateQueryStringSaga) can
// rewrite the URL and lose it. initTrackingEvents only runs after the user
// profile loads, which is far too late to still trust window.location.
let bootUrlSsid: string | null = isJourneyPath(currentPathname()) ? readSsidFromUrl() : null;
try {
  if (bootUrlSsid) {
    const { pathname, search, hash } = window.location;
    const parsed = qs.parse(search, { ignoreQueryPrefix: true });
    delete parsed[SSID_PARAM];
    const nextSearch = qs.stringify(parsed, { addQueryPrefix: true });
    // Raw replaceState: the router history isn't involved (or even created)
    // yet, and this must not trigger a navigation.
    window.history.replaceState(window.history.state, '', `${pathname}${nextSearch}${hash || ''}`);
  }
} catch {
  /* tracking must never throw */
}

const cacheSet = (id: string): void => {
  current = id;
  // The boot handoff is consumed the moment any journey id is committed.
  bootUrlSsid = null;
  writeSs(SUBSESSION_KEY, id);
};

/** Remove ssid from the address bar (history.replace, no navigation). */
export const stripSsidFromUrl = (): void => {
  try {
    const { pathname, search, hash } = window.location;
    const parsed = qs.parse(search, { ignoreQueryPrefix: true });
    if (!(SSID_PARAM in parsed)) return;
    delete parsed[SSID_PARAM];
    const nextSearch = qs.stringify(parsed, { addQueryPrefix: true });
    history.replace(`${pathname}${nextSearch}${hash || ''}`);
  } catch {
    /* tracking must never throw */
  }
};

/**
 * Adopt the active journey on app init. URL wins over cache, then the URL is
 * cleaned so ssid is never left visible in the address bar:
 *  - ssid present in URL  → adopt it (pin this tab to that journey), strip it
 *  - else cached value    → reuse it
 *  - else                 → mint a fresh one
 */
export const initSubSession = (): void => {
  try {
    if (!isTrackedUser()) return;
    // Prefer the boot-time capture: the live URL may have been rewritten.
    const urlSsid = bootUrlSsid ?? readSsidFromUrl();
    if (urlSsid) {
      cacheSet(urlSsid);
      stripSsidFromUrl();
      return;
    }
    const cached = readSs(SUBSESSION_KEY);
    if (cached) {
      cacheSet(cached);
      return;
    }
    cacheSet(uuidv4());
  } catch {
    /* tracking must never throw */
  }
};

export const getSubSessionId = (): string => {
  if (current) return current;
  const fromUrl = bootUrlSsid ?? readSsidFromUrl();
  if (fromUrl) {
    cacheSet(fromUrl);
    return fromUrl;
  }
  const cached = readSs(SUBSESSION_KEY);
  if (cached) {
    cacheSet(cached);
    return cached;
  }
  const minted = uuidv4();
  cacheSet(minted);
  return minted;
};

/**
 * Non-minting read of the active journey id: in-memory → URL → sessionStorage.
 * Returns null when no journey exists yet (does NOT mint one).
 */
export const peekSubSessionId = (): string | null =>
  current ?? bootUrlSsid ?? readSsidFromUrl() ?? readSs(SUBSESSION_KEY);

/** Mint a fresh journey (called by trackSearchOutcome). Replaces the old ssid. */
export const startNewSubSession = (): string => {
  const minted = uuidv4();
  cacheSet(minted);
  return minted;
};

const isInternalHref = (href: string): boolean => {
  if (!href) return false;
  // Relative path (router link) is internal.
  if (href.startsWith('/') && !href.startsWith('//')) return true;
  try {
    const url = new URL(href, window.location.origin);
    return url.origin === window.location.origin;
  } catch {
    return false;
  }
};

/**
 * Append the current ssid to an internal href so the journey survives a
 * manually-opened new tab (the only place sessionStorage cannot reach).
 * The receiving tab adopts it on init and strips it from the address bar.
 * External / other-origin hrefs and non-tracked users get the href unchanged.
 */
export const withSsid = (href: string): string => {
  try {
    if (!isTrackedUser()) return href;
    if (!isInternalHref(href)) return href;
    const url = new URL(href, window.location.origin);
    if (!isJourneyPath(url.pathname)) return href;
    const id = current || getSubSessionId();
    if (!id) return href;
    url.searchParams.set(SSID_PARAM, id);
    // Preserve relative form when the input was relative.
    if (href.startsWith('/')) {
      return `${url.pathname}${url.search}${url.hash}`;
    }
    return url.toString();
  } catch {
    return href;
  }
};

let linkDecoratorInstalled = false;

/**
 * Add the current ssid to internal links right before the user clicks,
 * so any "open in new tab" action (cmd/ctrl-click, middle-click,
 * right-click) carries it over too — not just links using withSsid().
 * Links stay unmodified until interaction; the new tab picks up the
 * ssid and removes it from the URL right away (see boot capture above).
 *
 * We use capture phase + mousedown/contextmenu to rewrite the href
 * before the browser uses it for the new tab. This doesn't affect
 * same-tab navigation — react-router's <Link> ignores href and
 * navigates via its `to` prop instead.
 */
export const installSsidLinkDecorator = (): void => {
  if (linkDecoratorInstalled) return;
  linkDecoratorInstalled = true;

  const decorate = (e: Event): void => {
    try {
      if (!isTrackedUser()) return;
      // Only hand the journey over from a journey page.
      if (!isJourneyPath(currentPathname())) return;
      const target: Element | null = e.target instanceof Element ? e.target : null;
      const anchor: HTMLAnchorElement | null = target ? target.closest('a[href]') : null;
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#')) return;
      const decorated = withSsid(href);
      if (decorated !== href) anchor.setAttribute('href', decorated);
    } catch {
      /* tracking must never throw */
    }
  };

  document.addEventListener('mousedown', decorate, true);
  document.addEventListener('contextmenu', decorate, true);
  // Keyboard activation (Enter on a focused link).
  document.addEventListener(
    'keydown',
    (e: KeyboardEvent) => {
      if (e.key === 'Enter') decorate(e);
    },
    true
  );
};

// Test-only reset of module state.
export const __resetSubSessionForTests = (): void => {
  current = null;
  bootUrlSsid = null;
};
